import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { asapScheduler, BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, observeOn } from 'rxjs/operators';
import { DeviceDetectorService } from 'ngx-device-detector';

import { MenuItem, ToastMessageOptions } from 'primeng/api';

// Services
import { AccountingSettingsService } from './accounting/accounting-settings.service';
import { ClientContextService } from './client/client-context.service';
import { ClientService } from './client/client.service';
import { ThemeService } from 'src/app/theme.service';

// Classes
import { AccountingSettings } from 'src/app/@shared/models/accounting/accounting-settings.model';
import { AgentMarker } from 'src/app/@shared/models/ai/agent-marker.model';
import { Client } from 'src/app/@shared/models/client/client.model';
import { User } from 'src/app/@shared/models/user/user.model';
import { ModelBase } from 'src/app/@shared/models/abstract/model-base.model';

// Enums
import { ActivitiesEnum } from 'src/app/@shared/enums/activities.enum';

// Utils
import { Logger } from './logger/logger.service';
import { MessageServiceBroker } from './message.service';
import { ServiceBase } from './service-base';

const LOGGER = new Logger('AppService');

/**
 * Cible de l'historique affiché depuis le fil d'ariane : l'objet consulté
 * sur la page courante et son service (endpoint générique `{id}/history/`)
 */
export interface HistoryTarget {
    source: ModelBase;
    service: ServiceBase;
}

/**
 * Mention « écrit par l'assistant » de l'objet consulté sur la page courante, affichée à
 * côté de la documentation (#1217), et la suite que la page donne à un geste.
 */
export interface AgentMarkerTarget {
    marker: AgentMarker;
    /** Le marqueur n'est plus, l'objet reste : la page le relit. */
    onCleared: () => void;
    /** L'objet n'existe plus : la page s'en va. */
    onUndone: () => void;
}
/**
 * Service de données globales de l'application
 */
@Injectable({
    providedIn: 'root'
})
export class AppService {
    private _router = inject(Router);
    private _accountingSettingsService = inject(AccountingSettingsService);
    private _clientContextService = inject(ClientContextService);
    private _clientService = inject(ClientService);
    private _deviceService = inject(DeviceDetectorService);
    private _messageServiceBroker = inject(MessageServiceBroker);
    private _themeService = inject(ThemeService);

    // CONSTANTS
    readonly CREDENTIALS_KEY = 'credentials';

    /*----------------------------------------------------------------------------------------
     * Données du service
     ----------------------------------------------------------------------------------------*/
    // Attributs de l'appareil
    public isLandscape: boolean = false;
    public isDesktop: boolean = false;
    public isPhone: boolean = false;
    public isTablet: boolean = false;
    public isAndroid: boolean = false;

    // Données du serveur
    public serverInfo: any = {};

    // Contexte global
    private _clients: Client[] = [];
    private _selectedClient: Client | null = null;
    private _user: User | null = null;

    // État global de l'application
    public breadcrumbItems: MenuItem[] = [];
    private _homeBreadcrumbItem: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };
    private _homeBreadcrumbClientId: number | null = null;
    /**
     * Accueil du fil d'Ariane. C'est un **getter** depuis #173 : le lien porte le
     * dossier courant, qu'une propriété évaluée à la construction figerait à vide.
     *
     * ⚠️ Il est mémoïsé : lié au template, il rendrait sinon une nouvelle référence
     * à chaque cycle de détection de changement. L'objet n'est reconstruit que
     * lorsque le dossier change.
     */
    public get homeBreadcrumbItem(): MenuItem {
        const clientId = this.selectedClient?.id ?? this._clientContextService.clientId;
        if (clientId !== this._homeBreadcrumbClientId) {
            this._homeBreadcrumbClientId = clientId;
            this._homeBreadcrumbItem = { icon: 'pi pi-home', routerLink: this.clientUrl('/dashboard') };
        }
        return this._homeBreadcrumbItem;
    }
    public agentMarkerTarget: AgentMarkerTarget | null = null;
    public historyTarget: HistoryTarget | null = null;
    private _menuOpen: boolean = true;
    public get menuOpen(): boolean {
        return this._menuOpen;
    }
    public set menuOpen(menuOpen: boolean) {
        this._menuOpen = menuOpen;
        setTimeout(() => {
            this._menuState$.next(menuOpen);
        }, 301);
    }
    public showNavButton: boolean = false;
    public theme: string = "biifor-light-blue";
    private _uiBlocked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * État de blocage global de l'UI (p-blockUI du shell).
     *
     * Les émissions sont différées d'une microtâche (`asapScheduler`) : de
     * nombreux appelants passent `uiBlocked` à `true` de façon synchrone
     * pendant un cycle de détection de changement, ce qui déclencherait sinon
     * une `NG0100 (ExpressionChangedAfterItHasBeenChecked)` sur le shell qui
     * lit ce flag tout en haut de l'arbre. Le report garantit que le
     * changement de valeur est appliqué dans un nouveau cycle.
     */
    public readonly uiBlocked$ = this._uiBlocked$.pipe(
        distinctUntilChanged(),
        observeOn(asapScheduler)
    );
    public get uiBlocked(): boolean {
        return this._uiBlocked$.value;
    }
    public set uiBlocked(value: boolean) {
        this._uiBlocked$.next(value);
    }
    private _accountEntryLinesPanelOpen: boolean = false;
    public get accountEntryLinesPanelOpen(): boolean {
        return this._accountEntryLinesPanelOpen;
    }
    public set accountEntryLinesPanelOpen(open: boolean) {
        this._accountEntryLinesPanelOpen = open;
        localStorage.setItem('accountEntryLinesPanelOpen', open.toString());
    }

    // Évènements
    private _clientsLoaded$: BehaviorSubject<Client[] | null> = new BehaviorSubject<Client[] | null>(null);
    public readonly clientsLoaded$ = this._clientsLoaded$.asObservable();
    private _clientSelected$: BehaviorSubject<Client | null> = new BehaviorSubject<Client | null>(null);
    public readonly clientSelected$ = this._clientSelected$.asObservable();
    private _menuState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.menuOpen);
    public readonly menuState$ = this._menuState$.asObservable();
    private _userLoaded$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
    public readonly userLoaded$ = this._userLoaded$.asObservable();

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get logoSrc(): string {
        return `assets/images/logo-${this.theme.includes('light') ? 'light' : 'dark'}.png`;
    }

    public get clients(): Client[] {
        return this._clients;
    }
    public set clients(clients: Client[]) {
        this._clients = clients;
        // Nouvelle valeur pour l'observable des organisations chargée
        this._clientsLoaded$.next(this._clients);
    }

    public get selectedClient(): Client | null {
        return this._selectedClient;
    }
    public set selectedClient(client: Client | null) {
        // Le contexte HTTP suit la façade applicative, qui suit elle-même l'URL (#173)
        this._clientContextService.clientId = client?.id ?? null;
        this._selectedClient = client;
        this._clientSelected$.next(client);
        if (!!client) {
            this._loadAuthorizations();
        } else {
            this.user!.authorizations = [];
        }
    }

    public get switchThemeIcon(): string {
        return this.theme.includes('light') ? 'pi-moon' : 'pi-sun';
    }

    public get user(): User | null {
        return this._user;
    }
    public set user(user: User | null) {
        if (!!user && user.id !== this._user?.id) {
            this._user = user;
            this._userLoaded$.next(this._user!);
        } else if (!user) {
            this.reset();
        }
    }

    /*----------------------------------------------------------------------------------------
     * Constructeur
     ----------------------------------------------------------------------------------------*/

    constructor() {
        // Sélection de l'organisation
        this._clientService.selected$.subscribe((client: Client) => {
            if (!!client) {
                this.selectedClient = client;
            }
        });
        // Type d'appareil
        this.isDesktop = this._deviceService.isDesktop();
        this.isPhone = this._deviceService.isMobile();
        this.isTablet = this._deviceService.isTablet();
        this.isAndroid = (this._deviceService.os ?? '').toLowerCase().includes('android');
        // Définition du theme
        let theme = localStorage.getItem('theme');
        if (!!theme) {
            this.theme = theme.replace('bfor', 'biifor');
            this._themeService.switchTheme(this.theme);
        }
        // État du menu
        let menuOpen = localStorage.getItem('menuOpen');
        if (!!menuOpen) {
            this.menuOpen = menuOpen === 'true';
        }
        // État du panel des écritures comptables
        let accountEntryLinesPanelOpen = localStorage.getItem('accountEntryLinesPanelOpen');
        if (!!accountEntryLinesPanelOpen) {
            this._accountEntryLinesPanelOpen = accountEntryLinesPanelOpen === 'true';
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public hideNavButton(): void {
        this.showNavButton = false;
    }

    public isFooterDisplayed(): boolean {
        return this.isClientSelected() && this.isPhone;
    }

    public isClientSelected(): boolean {
        return !!this.selectedClient;
    }

    /**
     * L'utilisateur peut-il gérer les pièces jointes de cet objet ?
     *
     * Le serveur garde l'ajout et le retrait d'une pièce par une activité **dédiée**,
     * `manage_attachments`, et non par `update` : la matrice statut × activité protège
     * le *contenu* d'un document, pas ses annexes. Gater sur `update` rendait les
     * pièces d'un devis ingérables dès sa validation, alors que c'est précisément là
     * qu'on y rattache le bon de commande signé.
     *
     * ⚠️ **Repli sur `update`.** Tous les porteurs ne déclarent pas l'activité : six
     * modèles seulement l'ont (factures de vente et d'achat, devis, emprunts, e-mails,
     * messages de discussion). Quand elle est absente des activités autorisées de
     * l'objet, on retombe sur `update`, comme le serveur.
     *
     * Le repli couvre aussi, sans distinction possible, le cas d'un porteur qui
     * déclare l'activité mais dont le **statut** l'interdit — un e-mail envoyé. Ce
     * n'est pas un trou : ces porteurs-là ferment `update` sur les mêmes statuts, le
     * repli répond donc `false` comme il le doit.
     *
     * Le paramétrage des ventes ne passe pas par ici : il n'a pas d'activités du tout,
     * et son droit est `customizing_update`.
     *
     * @param object L'objet qui porte les pièces jointes
     */
    public canManageAttachments(object: ModelBase): boolean {
        if (this.canUserPerform(ActivitiesEnum.ManageAttachments, object)) return true;
        if (object.canPerform(ActivitiesEnum.ManageAttachments)) return false;
        return this.canUserPerform(ActivitiesEnum.Update, object);
    }

    /**
     * Vérifie qu'un utilisateur peut effectuer une action sur un objet.
     * Croise les droits de l'utilisateur avec les activités autorisées sur l'objet côté métier.
     * Le code d'autorisation est dérivé automatiquement : <classname>_<activite> (ex: bankaccountline_delete)
     * @param activity L'activité à vérifier (ex: Activities.Delete, 'delete')
     * @param object L'objet sur lequel l'action doit être effectuée
     */
    public canUserPerform(activity: string, object: ModelBase): boolean {
        const modelName = (object.constructor as typeof ModelBase).CLASS_NAME.toLowerCase();
        const authorization = `${modelName}_${activity}`;
        if (!this.user?.hasAuthorization(authorization)) return false;
        return object.canPerform(activity);
    }

    public isPortrait(): boolean {
        return !this.isLandscape;
    }

    /**
     * Préfixe un chemin interne par le dossier courant (#173).
     *
     * C'est le **point de passage unique** pour construire un lien vers un écran
     * de dossier, en TypeScript comme en template :
     *
     * ```ts
     * this.router.navigate([this.appService.clientUrl('/sales/invoice'), invoice.id]);
     * ```
     * ```html
     * <a [routerLink]="appService.clientUrl('/settings/sales')">
     * ```
     *
     * Hors dossier, le chemin est renvoyé tel quel : la redirection de
     * compatibilité le rattrape et renvoie au sélecteur.
     *
     * @param path Chemin interne au dossier, commençant par `/`
     */
    public clientUrl(path: string): string {
        const clientId = this.selectedClient?.id ?? this._clientContextService.clientId;
        return clientId ? `/c/${clientId}${path}` : path;
    }

    /**
     * Navigation vers un écran d'un dossier donné.
     *
     * ⚠️ **Changer de dossier recharge complètement le document** (#173, arbitrage 6).
     * Les caches ne sont pas le seul état porté par les singletons : chaque
     * `ServiceBase` conserve un `_selected$`, et l'assistant IA garde une session et
     * son fil de conversation. Une navigation Angular les laisserait intacts — le
     * message suivant partirait avec le header du nouveau dossier et l'identifiant
     * de session de l'ancien. Le rechargement détruit tout état en mémoire par
     * construction, et le document se reconstruit depuis l'URL, qui fait foi.
     *
     * Le prix est un bootstrap complet, sur une action rare et délibérée. Ouvrir un
     * dossier depuis le sélecteur 'home', en revanche, reste une navigation : il n'y a
     * alors aucun état d'un dossier précédent à détruire.
     *
     * @param clientId Dossier visé
     * @param path Chemin interne au dossier, commençant par `/`
     */
    public navToClient(clientId: number, path: string = '/dashboard'): void {
        const url = `/c/${clientId}${path}`;
        const leavingAnotherClient = !!this.selectedClient && this.selectedClient.id !== clientId;

        if (leavingAnotherClient) {
            LOGGER.debug('Changement de dossier : rechargement complet', url);
            window.location.assign(url);
            return;
        }
        this._router.navigateByUrl(url);
    }

    /**
     * Retour au tableau de bord du dossier courant. Depuis #173 l'URL porte le
     * dossier : le détour par une route `dummy` en `skipLocationChange`, qui
     * servait à forcer la reconstruction du composant, n'a plus lieu d'être.
     */
    public navToDashboard(): void {
        const clientId = this.selectedClient?.id ?? this._clientContextService.clientId;
        if (!clientId) {
            this._router.navigate(['/home']);
            return;
        }
        this.navToClient(clientId);
    }

    /**
     * Relit les réglages comptables du dossier courant sur le serveur (#1258).
     *
     * `selectedClient.accountingSettings` est chargé une seule fois, à l'entrée dans le
     * dossier : un réglage modifié depuis un autre onglet, une autre session ou un autre
     * intervenant (compte collectif repointé puis supprimé, par exemple) n'y est pas vu.
     * À appeler à l'ouverture d'un écran qui pré-remplit un compte depuis ces réglages.
     *
     * L'instance lue n'est pas sélectionnée dans le service : le formulaire des réglages,
     * à l'écoute de `selected$`, ne doit pas être réinitialisé par cette relecture.
     *
     * @returns Les réglages relus, ou `null` si la lecture a échoué ou si le dossier a changé entre-temps
     */
    public refreshAccountingSettings$(): Observable<AccountingSettings | null> {
        const client = this.selectedClient;
        if (!client) {
            return of(null);
        }
        return this._accountingSettingsService.get$(client.id, false).pipe(
            map((settings: any) => {
                // `get$` renvoie `false` quand la lecture échoue
                if (!settings || this.selectedClient?.id !== client.id) {
                    return null;
                }
                client.accountingSettings = settings;
                return settings as AccountingSettings;
            })
        );
    }

    /**
     * Ré-initialisation des données de l'application
     */
    public reset(): void {
        this._user = null;
        this._userLoaded$.next(this._user);
        this._clients = [];
        this._clientsLoaded$.next(null);
        this.unselectClient();

        this.agentMarkerTarget = null;
        this.breadcrumbItems = [];
        this.historyTarget = null;
        this.menuOpen = true;
        this.showNavButton = false;
        this.theme = "biifor-light-blue";
        this.uiBlocked = false;
    }


    /**
     * Sélection d'un dossier. Depuis #173 c'est une **navigation** : le chargement
     * du dossier est le fait de `ClientContextGuard`, déclenché par l'URL.
     */
    public selectClient(client: Client) {
        if (!this.selectedClient || client.id !== this.selectedClient.id) {
            LOGGER.debug('Sélection du client', client.name);
            this.navToClient(client.id);
        }
    }

    public showMessage(message: ToastMessageOptions): void {
        this._messageServiceBroker.sendMessage(message);
    }

    public switchTheme(): void {
        this.theme = this.theme === 'biifor-light-blue' ? 'biifor-dark-blue' : 'biifor-light-blue';
        this._themeService.switchTheme(this.theme);
        // Sauvegarde du theme dans un cookie
        localStorage.setItem('theme', this.theme);
    }

    public toggleMenu(): void {
        this.menuOpen = !this.menuOpen;
        // Sauvegarde de l'état du menu dans un cookie
        localStorage.setItem('menuOpen', this.menuOpen.toString());
    }

    public unselectClient() {
        this._selectedClient = null;
        this._clientSelected$.next(this._selectedClient);
        this._clientService.unselect();
        LOGGER.debug('Retrait du dossier du contexte');
        this._clientContextService.clientId = null;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _loadAuthorizations(): void {
        // Récupération des autorisations de l'utilisateur
        this._clientService.getRelated$(this.selectedClient, 'authorizations').subscribe((authorizations: any) => {
            this.userLoaded$.subscribe(user => {
                if (!!user) {
                    user.authorizations = authorizations;
                }
            });
        });
    }
}
